import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Chips, Spoiler } from '@components/ui';
import { scrollParentToChild } from '@helpers/ScrollHelpers';

import { useRouteQueryAnalyticsListProps } from '@hooks/useRouteQueryAnalyticsListProps';

import type { IListingCategories } from 'types';

import styles from './ProductsListingNavigation.module.scss';

interface Props {
  className?: string,
  categories?: IListingCategories,
  categoryCode?: string,
  getSubcategoryLink?: (categoryCode?: string, subCategoryCode?: string) => string
}

const ProductsListingNavigation = React.forwardRef<HTMLDivElement, Props>((props: Props, ref: React.Ref<HTMLDivElement>) => {
  const {
    className,
    //
    categories = [],
    categoryCode,
    //
    getSubcategoryLink
  } = props;

  // Refs

  const chipsRef = React.useRef<HTMLUListElement>(null);
  const selectedSubcategoryRef = React.useRef<HTMLLIElement>(null);

  // Hooks

  const { persist: persistAnalyticsListProps } = useRouteQueryAnalyticsListProps();

  const [chipsExpanded, setChipsExpanded] = React.useState(false);

  // Effects

  React.useEffect(() => {
    if (!selectedSubcategoryRef?.current) return;

    if (selectedSubcategoryRef.current.offsetTop >= 40) {
      setChipsExpanded(true);
    }

    if (!chipsRef?.current) return;

    scrollParentToChild(chipsRef.current, selectedSubcategoryRef.current);
  }, [categories, chipsRef.current]);

  // Props

  const categoryChips = categories.map((category) => {
    const {
      code, name, selected
    } = category;

    const url = getSubcategoryLink ? getSubcategoryLink(categoryCode, code) : '';

    return {
      slug: code,
      name,
      href: url,
      selected
    };
  });

  // Handlers

  const handleItemClick = () => {
    persistAnalyticsListProps();
  };

  // Render

  return (
    // @ts-ignore TODO - remove when Spoiler is migrated to TS
    <Spoiler
      ref={ref}
      id={categoryCode}
      className={classnames(styles.root, className)}
      expandClassName={styles.expandBtn}
      height={40}
      mobile={false}
      expanded={chipsExpanded}
    >
      <Chips
        ref={chipsRef}
        className={styles.chips}
        items={categoryChips}
        selectedRef={selectedSubcategoryRef}
        onChipClick={handleItemClick}
      />
    </Spoiler>
  );
});

export default ProductsListingNavigation;
